import React, { useState, useEffect } from 'react';
import * as dpService from 'features/dp/services/dpService';
import HoleriteModal from './HoleriteModal';

/**
 * ResumoFolha – "Resumo" sub-tab inside Folha de Pagamento.
 * Shows a per-employee summary table with iFood columns, CSV export,
 * inline observações, and click-on-row to open the holerite modal.
 *
 * Props:
 *   folhas – array of folha objects (used to populate the selector)
 */

const fmtBRL = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const compLabel = (f) => {
  if (!f?.competencia) return '—';
  const [y, mo] = f.competencia.split('-');
  const ms = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return (ms[parseInt(mo, 10) - 1] || mo) + '/' + y;
};

export default function ResumoFolha({ folhas }) {
  const [folhaId, setFolhaId] = useState(null);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [obs, setObs] = useState('');
  const [salvandoObs, setSalvandoObs] = useState(false);
  const [obsStatus, setObsStatus] = useState(null);
  const [holOpen, setHolOpen] = useState(false);
  const [holFuncId, setHolFuncId] = useState(null);

  // Auto-select first folha
  useEffect(() => {
    if (folhas?.length > 0 && !folhaId) {
      setFolhaId(folhas[0].id);
    }
  }, [folhas, folhaId]);

  // Fetch resumo when folha changes
  useEffect(() => {
    if (!folhaId) return;
    setLoading(true);
    setDados(null);

    dpService
      .getResumo(folhaId)
      .then((r) => {
        const d = r.data.data;
        setDados(d);
        setObs(d?.folha?.observacoes ?? '');
      })
      .catch(() => setDados(null))
      .finally(() => setLoading(false));
  }, [folhaId]);

  // ── Save observações ──
  const salvarObs = () => {
    setSalvandoObs(true);
    setObsStatus(null);

    dpService
      .salvarObservacao(folhaId, { observacoes: obs })
      .then(() => setObsStatus('ok'))
      .catch(() => setObsStatus('erro'))
      .finally(() => setSalvandoObs(false));
  };

  // ── CSV export ──
  const exportarCSV = () => {
    if (!dados?.linhas) return;

    const headers = [
      'Nº', 'PIX', 'NOME', 'FUNÇÃO', 'LÍQUIDO',
      'IFOOD BONIFICAÇÃO', 'IFOOD FIXO', 'IFOOD TOTAL', 'TOTAL LÍQUIDO/CARTÃO',
    ];

    const rows = dados.linhas.map((l, i) => [
      i + 1, l.pix, l.nome, l.funcao, l.liquido,
      l.ifood_bonificacao, l.ifood_fixo, l.ifood_total, l.total_com_ifood,
    ]);

    const t = dados.totais;
    rows.push([
      'TOTAIS', '', '', '', t.liquido,
      t.ifood_bonificacao, t.ifood_fixo, t.ifood_total, t.total_com_ifood,
    ]);

    const csv = [headers, ...rows]
      .map((r) => r.map((v) => '"' + String(v).replace(/"/g, '""') + '"').join(','))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'resumo-folha-' + (folhaId || '') + '.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* ── Selector + Export ── */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium text-[color:var(--text-muted)]">Folha:</label>
        <select
          value={folhaId || ''}
          onChange={(e) => setFolhaId(Number(e.target.value))}
          className="border border-[color:var(--border)] rounded-lg px-3 py-1.5 text-sm bg-[color:var(--surface)] text-[color:var(--text)]"
        >
          {folhas?.map((f) => (
            <option key={f.id} value={f.id}>
              {compLabel(f)}
            </option>
          ))}
        </select>
        <button
          onClick={exportarCSV}
          disabled={!dados}
          className="ml-auto px-3 py-1.5 text-xs border border-green-600 text-green-700 rounded-lg hover:bg-green-50 disabled:opacity-40"
        >
          ⬇ Exportar CSV
        </button>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full" />
        </div>
      ) : dados?.linhas ? (
        <>
          {/* Summary table */}
          <div className="overflow-x-auto rounded-xl border border-[color:var(--border)]">
            <table className="w-full text-xs min-w-[960px]">
              <thead>
                <tr className="bg-[color:var(--surface-muted)] border-b border-[color:var(--border)]">
                  <th className="p-2 text-center w-8 font-semibold text-[color:var(--text-muted)]">Nº</th>
                  <th className="p-2 text-left font-semibold text-[color:var(--text-muted)]">PIX</th>
                  <th className="p-2 text-left font-semibold text-[color:var(--text-muted)]">NOME</th>
                  <th className="p-2 text-left font-semibold text-[color:var(--text-muted)]">FUNÇÃO</th>
                  <th className="p-2 text-right font-semibold text-[color:var(--text-muted)]">LÍQUIDO</th>
                  <th className="p-2 text-right font-semibold text-orange-600">IFOOD BONIF.</th>
                  <th className="p-2 text-right font-semibold text-orange-600">IFOOD FIXO</th>
                  <th className="p-2 text-right font-semibold text-orange-600">IFOOD TOTAL</th>
                  <th className="p-2 text-right font-semibold text-teal-700">TOTAL LIQ./CARTÃO</th>
                  <th className="p-2 text-left font-semibold text-[color:var(--text-muted)] min-w-[180px]">OBSERVAÇÃO</th>
                </tr>
              </thead>

              <tbody>
                {dados.linhas.map((l, i) => (
                  <tr
                    key={l.funcionario_id}
                    onClick={() => {
                      setHolFuncId(l.funcionario_id);
                      setHolOpen(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    className="border-b border-[color:var(--border)] hover:bg-[color:var(--surface-muted)]"
                  >
                    <td className="p-2 text-center text-[color:var(--text-muted)]">{i + 1}</td>
                    <td className="p-2 font-mono text-xs text-[color:var(--text-muted)]">{l.pix}</td>
                    <td className="p-2 font-medium text-[color:var(--text)]">{l.nome}</td>
                    <td className="p-2 text-[color:var(--text-muted)]">{l.funcao}</td>
                    <td className="p-2 text-right font-medium text-[color:var(--text)]">
                      {fmtBRL(l.liquido)}
                    </td>
                    <td
                      className={
                        'p-2 text-right ' +
                        (l.ifood_bonificacao > 0
                          ? 'text-orange-600 font-medium'
                          : 'text-[color:var(--text-muted)]')
                      }
                    >
                      {l.ifood_bonificacao > 0 ? fmtBRL(l.ifood_bonificacao) : '—'}
                    </td>
                    <td
                      className={
                        'p-2 text-right ' +
                        (l.ifood_fixo > 0
                          ? 'text-orange-600 font-medium'
                          : 'text-[color:var(--text-muted)]')
                      }
                    >
                      {l.ifood_fixo > 0 ? fmtBRL(l.ifood_fixo) : '—'}
                    </td>
                    <td
                      className={
                        'p-2 text-right ' +
                        (l.ifood_total > 0
                          ? 'text-orange-600 font-semibold'
                          : 'text-[color:var(--text-muted)]')
                      }
                    >
                      {l.ifood_total > 0 ? fmtBRL(l.ifood_total) : '—'}
                    </td>
                    <td className="p-2 text-right font-bold text-teal-700">
                      {fmtBRL(l.total_com_ifood)}
                    </td>
                    <td className="p-2">
                      <input
                        type="text"
                        defaultValue={l.observacoes || ''}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => {
                          const v = e.target.value;
                          if (v !== (l.observacoes || '')) {
                            dpService
                              .salvarObservacaoResumo(l.funcionario_id, {
                                competencia: dados.folha.competencia,
                                observacoes: v,
                              })
                              .catch(() => {});
                          }
                        }}
                        className="w-full px-2 py-1 text-xs border border-[color:var(--border)] rounded bg-[color:var(--surface)] text-[color:var(--text)] focus:outline-none focus:ring-1 focus:ring-teal-500"
                        placeholder="Obs..."
                      />
                    </td>
                  </tr>
                ))}
              </tbody>

              <tfoot>
                <tr className="bg-[color:var(--surface-muted)] border-t-2 border-[color:var(--border)] font-bold">
                  <td className="p-2 text-center text-xs text-[color:var(--text-muted)]" colSpan={4}>
                    TOTAIS
                  </td>
                  <td className="p-2 text-right text-[color:var(--text)]">
                    {fmtBRL(dados.totais.liquido)}
                  </td>
                  <td className="p-2 text-right text-orange-600">
                    {dados.totais.ifood_bonificacao > 0 ? fmtBRL(dados.totais.ifood_bonificacao) : '—'}
                  </td>
                  <td className="p-2 text-right text-orange-600">
                    {dados.totais.ifood_fixo > 0 ? fmtBRL(dados.totais.ifood_fixo) : '—'}
                  </td>
                  <td className="p-2 text-right text-orange-600">
                    {dados.totais.ifood_total > 0 ? fmtBRL(dados.totais.ifood_total) : '—'}
                  </td>
                  <td className="p-2 text-right text-teal-700">
                    {fmtBRL(dados.totais.total_com_ifood)}
                  </td>
                  <td className="p-2" />
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Observações block */}
          <div className="border border-[color:var(--border)] rounded-xl p-3 space-y-2">
            <label className="block text-xs font-medium text-[color:var(--text-muted)]">
              Observações:
            </label>
            <textarea
              value={obs}
              onChange={(e) => setObs(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)] resize-y focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Observações sobre esta folha..."
            />
            <div className="flex items-center gap-2 justify-end">
              {obsStatus === 'ok' && (
                <span className="text-xs text-green-600">Salvo!</span>
              )}
              {obsStatus === 'erro' && (
                <span className="text-xs text-red-500">Erro ao salvar</span>
              )}
              <button
                onClick={salvarObs}
                disabled={salvandoObs || !folhaId}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
              >
                {salvandoObs ? 'Salvando...' : 'Salvar Observações'}
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-12 text-[color:var(--text-muted)] text-sm">
          Selecione uma folha para ver o resumo.
        </div>
      )}

      {/* ── Holerite modal (triggered by row click) ── */}
      {holOpen && (
        <HoleriteModal
          folhaId={folhaId}
          onFechar={() => setHolOpen(false)}
          funcionarioInicial={holFuncId}
        />
      )}
    </div>
  );
}
